
import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';

export default function Navbar() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [showServicesDropdown, setShowServicesDropdown] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const services = [
    { name: 'Hood & Exhaust Cleaning', icon: 'ri-restaurant-line', link: '/services/hood-exhaust-cleaning' },
    { name: 'Fan & Filter Maintenance', icon: 'ri-settings-3-line', link: '/services/fan-filter-maintenance' },
    { name: 'Duct System Cleaning', icon: 'ri-wind-line', link: '/services/duct-system-cleaning' },
    { name: 'Fire Suppression Inspection', icon: 'ri-fire-line', link: '/services/fire-suppression' },
    { name: 'Pre-Opening Deep Clean', icon: 'ri-checkbox-circle-line', link: '/services/pre-opening-clean' },
    { name: 'Emergency Cleaning Services', icon: 'ri-alarm-warning-line', link: '/services/emergency-cleaning' }
  ];

  return (
    <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${isScrolled ? 'bg-white shadow-lg' : 'bg-transparent'}`}>
      <div className="max-w-[1400px] mx-auto px-4 md:px-8">
        <div className="flex items-center justify-between h-[70px] md:h-[90px]">
          {/* Logo */}
          <Link to="/" className="flex items-center">
            <img 
              src="https://static.readdy.ai/image/2733261fde4eb6ca5d5b92336d650a8a/b6352a008471ad7da159707c3313e87e.png" 
              alt="Expo Hood Cleaning Logo" 
              className="h-12 md:h-16 w-auto"
            />
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden lg:flex items-center gap-9">
            <a href="/#home" className={`text-[15px] font-medium transition-colors hover:text-[#1E5BA8] whitespace-nowrap cursor-pointer ${isScrolled ? 'text-gray-800' : 'text-white'}`}>
              Home
            </a>
            
            {/* Services Dropdown */}
            <div 
              className="relative"
              onMouseEnter={() => setShowServicesDropdown(true)}
              onMouseLeave={() => setShowServicesDropdown(false)}
            >
              <button className={`text-[15px] font-medium transition-colors hover:text-[#1E5BA8] whitespace-nowrap cursor-pointer flex items-center gap-1 ${isScrolled ? 'text-gray-800' : 'text-white'}`}>
                Services
                <i className={`ri-arrow-down-s-line text-lg ${showServicesDropdown ? 'rotate-180' : ''} transition-transform`}></i>
              </button>
              
              <AnimatePresence>
                {showServicesDropdown && (
                  <motion.div
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: 10 }}
                    transition={{ duration: 0.2 }}
                    className="absolute top-full left-0 mt-2 w-[280px] bg-white rounded-lg shadow-xl py-3 border border-gray-100"
                  >
                    {services.map((service, index) => (
                      <Link
                        key={index}
                        to={service.link}
                        className="flex items-center gap-3 px-5 py-3 hover:bg-gray-50 transition-colors cursor-pointer"
                      >
                        <div className="w-9 h-9 flex items-center justify-center">
                          <i className={`${service.icon} text-xl text-[#1E5BA8]`}></i>
                        </div>
                        <span className="text-[14px] text-gray-700 font-medium">{service.name}</span>
                      </Link>
                    ))}
                  </motion.div>
                )}
              </AnimatePresence>
            </div>

            <a href="/#service-area" className={`text-[15px] font-medium transition-colors hover:text-[#1E5BA8] whitespace-nowrap cursor-pointer ${isScrolled ? 'text-gray-800' : 'text-white'}`}>
              Service Area
            </a>
            <a href="/#about" className={`text-[15px] font-medium transition-colors hover:text-[#1E5BA8] whitespace-nowrap cursor-pointer ${isScrolled ? 'text-gray-800' : 'text-white'}`}>
              About Us
            </a>
            <a href="/#rewards" className={`text-[15px] font-medium transition-colors hover:text-[#1E5BA8] whitespace-nowrap cursor-pointer ${isScrolled ? 'text-gray-800' : 'text-white'}`}>
              Rewards
            </a>
            <a href="/#contact" className={`text-[15px] font-medium transition-colors hover:text-[#1E5BA8] whitespace-nowrap cursor-pointer ${isScrolled ? 'text-gray-800' : 'text-white'}`}>
              Contact
            </a>
          </div>

          {/* Desktop Right Side CTAs */}
          <div className="hidden lg:flex items-center gap-4">
            <a 
              href="https://www.facebook.com/profile.php?id=61571308832734" 
              target="_blank" 
              rel="nofollow noopener noreferrer"
              className="w-[50px] h-[50px] rounded-full bg-[#1E5BA8] flex items-center justify-center hover:bg-[#164a8f] transition-colors cursor-pointer"
            >
              <i className="ri-facebook-fill text-white text-2xl"></i>
            </a>
            <div className="flex flex-col gap-0.5">
              <a 
                href="tel:+15743299050" 
                className={`flex items-center gap-2 text-[14px] font-medium whitespace-nowrap cursor-pointer ${isScrolled ? 'text-gray-800' : 'text-white'}`}
              >
                <i className="ri-phone-line text-base"></i>
                (574) 329-9050
              </a>
              <a 
                href="tel:+15746221711" 
                className={`flex items-center gap-2 text-[14px] font-medium whitespace-nowrap cursor-pointer ${isScrolled ? 'text-gray-800' : 'text-white'}`}
              >
                <i className="ri-phone-line text-base"></i>
                (574) 622-1711
              </a>
            </div>
            <a 
              href="/#book-appointment"
              className="px-6 py-3 bg-[#1E5BA8] text-white rounded-lg text-[15px] font-semibold hover:bg-[#164a8f] transition-colors whitespace-nowrap cursor-pointer"
            >
              Book Appointment
            </a>
          </div>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            className={`lg:hidden w-10 h-10 flex items-center justify-center ${isScrolled ? 'text-gray-800' : 'text-white'}`}
          >
            <i className={`${isMobileMenuOpen ? 'ri-close-line' : 'ri-menu-line'} text-3xl`}></i>
          </button>
        </div>

        {/* Mobile Menu */}
        <AnimatePresence>
          {isMobileMenuOpen && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              className="lg:hidden bg-white border-t border-gray-200 overflow-hidden"
            >
              <div className="py-4 space-y-1">
                <a href="/#home" className="block px-4 py-3 text-gray-800 hover:bg-gray-50 cursor-pointer">
                  Home
                </a>
                
                {/* Mobile Services */}
                <div>
                  <button
                    onClick={() => setShowServicesDropdown(!showServicesDropdown)}
                    className="w-full flex items-center justify-between px-4 py-3 text-gray-800 hover:bg-gray-50 cursor-pointer"
                  >
                    <span>Services</span>
                    <i className={`ri-arrow-down-s-line text-lg ${showServicesDropdown ? 'rotate-180' : ''} transition-transform`}></i>
                  </button>
                  {showServicesDropdown && (
                    <div className="bg-gray-50 py-2">
                      {services.map((service, index) => (
                        <Link
                          key={index}
                          to={service.link}
                          onClick={() => setIsMobileMenuOpen(false)}
                          className="flex items-center gap-3 px-8 py-3 hover:bg-gray-100 cursor-pointer"
                        >
                          <i className={`${service.icon} text-lg text-[#1E5BA8]`}></i>
                          <span className="text-[14px] text-gray-700">{service.name}</span>
                        </Link>
                      ))}
                    </div>
                  )}
                </div>

                <a href="/#service-area" className="block px-4 py-3 text-gray-800 hover:bg-gray-50 cursor-pointer">
                  Service Area
                </a>
                <a href="/#about" className="block px-4 py-3 text-gray-800 hover:bg-gray-50 cursor-pointer">
                  About Us
                </a>
                <a href="/#rewards" className="block px-4 py-3 text-gray-800 hover:bg-gray-50 cursor-pointer">
                  Rewards
                </a>
                <a href="/#contact" className="block px-4 py-3 text-gray-800 hover:bg-gray-50 cursor-pointer">
                  Contact
                </a>

                {/* Mobile CTAs */}
                <div className="px-4 pt-4 space-y-3 border-t border-gray-200">
                  <a 
                    href="tel:+15743299050" 
                    className="flex items-center justify-center gap-2 w-full py-3 bg-gray-100 text-gray-800 rounded-lg font-medium cursor-pointer"
                  >
                    <i className="ri-phone-line text-lg"></i>
                    (574) 329-9050
                  </a>
                  <a 
                    href="tel:+15746221711" 
                    className="flex items-center justify-center gap-2 w-full py-3 bg-gray-100 text-gray-800 rounded-lg font-medium cursor-pointer"
                  >
                    <i className="ri-phone-line text-lg"></i>
                    (574) 622-1711
                  </a>
                  <a 
                    href="/#book-appointment"
                    className="block text-center w-full py-3 bg-[#1E5BA8] text-white rounded-lg font-semibold cursor-pointer"
                  >
                    Book Appointment
                  </a>
                  <a 
                    href="https://www.facebook.com/profile.php?id=61571308832734" 
                    target="_blank" 
                    rel="nofollow noopener noreferrer"
                    className="flex items-center justify-center gap-2 w-full py-3 bg-[#1E5BA8] text-white rounded-lg font-medium cursor-pointer"
                  >
                    <i className="ri-facebook-fill text-xl"></i>
                    Follow Us
                  </a>
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </nav>
  );
}
